<script setup lang="ts">

import { usePlanetStore } from '@/stores/planetStore.ts'
import { computed, defineProps } from 'vue'
import StandardCard from '@/components/Elements/Cards/StandardCard.vue'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePQAStore } from '@/stores/PQAStore.ts'
import Backend from '@/models/backend.ts'
import ToggleSwitch from '@/components/Interface/ToggleSwitch/ToggleSwitch.vue'

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores / models
 */
const planetStore = usePlanetStore();
const gameStore = useGameStore();
const PQAStore = usePQAStore()
const backend = new Backend()

/**
 * Functions
 */
const createPQA = () => {
  gameStore.modalType='createPQA';
};

const enablePQA = (id:number) => {
  backend.planetSetPQA([props.planetId],id).then(() => {
    gameStore.setTinySuccessToastMessage('Building queue enabled');
  })
};

const disablePQA = () => {
  backend.planetSetPQA([props.planetId],0).then(() => {
    gameStore.setTinySuccessToastMessage('Building queue disabled');
  })
};

const configurePQA = (id:number) => {
  gameStore.modalType = 'configPQA';
  gameStore.modalData = id;
};

/**
 * Computed
 */
const planetData = computed(() => planetStore.findPlanetById(props.planetId)!)

const allQueues = computed(() => {
  return [...PQAStore.queues.values()];
});

const isQueueActive = (queueId: number) => {
  return planetData.value.planetQueueAutomationId === queueId;
};

const toggleQueue = (queueId: number) => {
  if (isQueueActive(queueId)) {
    disablePQA();
  } else {
    enablePQA(queueId);
  }
};

</script>

<template>

  <StandardCard v-if="allQueues.length > 0" title="Automated Building Queues" help="Automated Building queues allow you to automate the construction of buildings on a planet, you setup a queue, and assign it to a planet. Then everyhing on that queue (if available) will be built in sequence. Note that this is different to the reqular building queues that planets have." topPadding="2">
    <template v-for="(pqa, index) in allQueues" :key="pqa.id">
      <div :class="['flex justify-between items-center', { 'pt-2 mt-2 border-t border-t-slate-700': index > 0 }]">
        <div class="flex-grow flex items-center">
          <div class="text-subtext">{{ pqa.name }}</div>
        </div>
        <div class="flex items-center gap-2">
          <div class="flex items-center -mt-1">
            <ToggleSwitch :model-value="isQueueActive(pqa.id)" @update:model-value="toggleQueue(pqa.id)" />
          </div>
          <div class="flex items-center">
            <ActionButton @click="configurePQA(pqa.id)" :for-icon="true">
              <GenericIcon tooltip="Configure" type="cog" />
            </ActionButton>
          </div>
        
        </div>
      </div>
    </template>
  </StandardCard>

  <StandardCard top-padding="2">
    <ActionButton @click="createPQA" :full-width="true">Create New Automated Building Queue</ActionButton>
  </StandardCard>

</template>

<style scoped lang="scss">

</style>